import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import OabsEscritorio from "@/components/OabsEscritorio";
import IdentidadeCard from "@/components/IdentidadeCard";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Building2, UserPlus, Mail, Copy, X, LogOut, Shield, ArrowRightLeft } from "lucide-react";

// Lote 156 — Escritório (E3 do Corporativo, fundação): quem faz parte e com que papel.
// Administrador convida por e-mail, muda papéis e remove; os demais só veem a equipe.
// Lote 157: cada processo tem um responsável; o administrador troca e transfere. Os dados seguem na conta de quem cadastrou.
const PAPEL_DICA = {
  admin: "Convida, remove e muda papéis.",
  advogado: "Trabalha com tudo do escritório.",
  apoio: "Agenda e registra; não apaga nem exporta.",
};
// Lote 164: a Controladoria é dos Administradores; o administrador pode liberar para quem quiser (chave em cada pessoa).

export default function Escritorio() {
  const { checkAuth: refresh } = useAuth() || {};
  const [d, setD] = useState(null);
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [papel, setPapel] = useState("advogado");
  const [busy, setBusy] = useState(false);
  const [confirmar, setConfirmar] = useState(null);     // user_id aguardando "confirmar remoção"
  const [ultimoLink, setUltimoLink] = useState(null);   // {email, link, enviado}
  const [de, setDe] = useState("");                     // Lote 157: transferência de processos
  const [para, setPara] = useState("");
  const [confirmaTransf, setConfirmaTransf] = useState(false);

  const load = async () => {
    try { setD((await http.get("/escritorio")).data); } catch { setD({ erro: true }); }
  };
  useEffect(() => { load(); }, []);
  const depois = async () => { await load(); if (refresh) { try { await refresh(); } catch { /* menu se atualiza no próximo carregamento */ } } };
  const falha = (e, padrao) => toast.error(e?.response?.data?.detail || padrao);

  const criar = async (ev) => {
    ev.preventDefault(); setBusy(true);
    try { await http.post("/escritorio", { nome }); toast.success("Escritório criado"); await depois(); } catch (e) { falha(e, "Não foi possível criar"); } finally { setBusy(false); }
  };
  const convidar = async (ev) => {
    ev.preventDefault(); setBusy(true);
    try {
      const r = (await http.post("/escritorio/convites", { email, papel })).data;
      setUltimoLink({ email, link: r.link, enviado: r.email_enviado });
      toast.success(r.email_enviado ? "Convite enviado por e-mail" : "Convite criado — o e-mail não saiu; copie o link");
      setEmail(""); await load();
    } catch (e) { falha(e, "Não foi possível convidar"); } finally { setBusy(false); }
  };
  const copiar = async (link) => { try { await navigator.clipboard.writeText(link); toast.success("Link copiado"); } catch { toast.error("Não consegui copiar; selecione o link e copie"); } };
  const mudarPapel = async (id, novo) => { try { await http.put(`/escritorio/membros/${id}`, { papel: novo }); toast.success("Papel atualizado"); await depois(); } catch (e) { falha(e, "Não foi possível mudar"); await load(); } };
  const remover = async (id) => { try { await http.delete(`/escritorio/membros/${id}`); toast.success("Pessoa removida do escritório"); setConfirmar(null); await depois(); } catch (e) { falha(e, "Não foi possível remover"); } };
  const transferir = async () => {
    setBusy(true);
    try { const r = (await http.post("/escritorio/transferir", { de, para })).data; toast.success(`${r.transferidos} ${r.transferidos === 1 ? "processo passou" : "processos passaram"} para o novo responsável`); setConfirmaTransf(false); setDe(""); setPara(""); await load(); }
    catch (e) { falha(e, "Não foi possível transferir"); } finally { setBusy(false); }
  };
  const indicarControladoria = async (id, ativo) => {
    try { await http.put(`/escritorio/membros/${id}/controladoria`, { ativo }); toast.success(ativo ? "Agora essa pessoa vê a Controladoria" : "Acesso à Controladoria retirado"); await load(); }
    catch (e) { falha(e, "Não foi possível salvar"); await load(); }
  };
  const cancelar = async (id) => { try { await http.delete(`/escritorio/convites/${id}`); await load(); } catch (e) { falha(e, "Não foi possível cancelar"); } };
  const sair = async () => { try { await http.post("/escritorio/sair"); toast.success("Você saiu do escritório"); await depois(); } catch (e) { falha(e, "Não foi possível sair"); } };

  if (!d) return <p className="p-6 text-sm text-stone-500">Carregando…</p>;
  const cab = (
    <div className="mb-6">
      <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-gold-600 mb-1">Escritório</div>
      <h1 className="font-display text-3xl font-bold text-stone-900" data-testid="escritorio-titulo">{d.existe ? d.org.nome : "Seu escritório"}</h1>
    </div>
  );

  if (d.erro) return <div className="max-w-3xl mx-auto p-4 md:p-8">{cab}<p className="text-sm text-red-700">Não consegui carregar. Tente de novo em instantes.</p></div>;

  if (!d.existe) {
    return (
      <div className="max-w-3xl mx-auto p-4 md:p-8" data-testid="escritorio-vazio">
        {cab}
        {d.pode_criar ? (
          <form onSubmit={criar} className="rounded-xl border border-stone-200 bg-white p-5 space-y-3 max-w-md">
            <p className="text-sm text-stone-600">Crie o escritório para convidar sócios, advogados e apoio. Você será o administrador.</p>
            <Input data-testid="escritorio-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do escritório" maxLength={80} />
            <Button type="submit" disabled={busy || nome.trim().length < 2} data-testid="escritorio-criar" className="bg-stone-900 text-white hover:bg-stone-800">Criar escritório</Button>
          </form>
        ) : <p className="text-sm text-stone-600">Você não faz parte de um escritório. Peça um convite ao administrador.</p>}
      </div>
    );
  }

  const admin = d.meu_papel === "admin";
  const rotulo = (v) => d.papeis.find((p) => p.valor === v)?.rotulo || v;
  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8" data-testid="escritorio">
      {cab}
      <p className="text-sm text-stone-600 mb-4" data-testid="escritorio-meu-papel">Seu papel: <b>{rotulo(d.meu_papel)}</b>. {PAPEL_DICA[d.meu_papel]}</p>

      <section aria-label="Equipe" className="rounded-xl border border-stone-200 bg-white divide-y divide-stone-100" data-testid="escritorio-equipe">
        {d.membros.map((m) => (
          <div key={m.user_id} data-testid={`membro-${m.user_id}`} className="p-4 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-stone-900 truncate">{m.nome}{m.eu && <span className="ml-2 text-xs font-normal text-stone-500">(você)</span>}</div>
              <div className="text-xs text-stone-500 break-words">{m.email}</div>
            </div>
            {admin ? (
              <select data-testid={`papel-${m.user_id}`} value={m.papel} onChange={(e) => mudarPapel(m.user_id, e.target.value)} aria-label={`Papel de ${m.nome}`}
                className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm">
                {d.papeis.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
              </select>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-700">{m.papel === "admin" && <Shield className="w-3 h-3" />}{rotulo(m.papel)}</span>
            )}
            {admin && m.papel !== "admin" && (
              <label className="inline-flex items-center gap-2 text-xs text-stone-600 cursor-pointer select-none" data-testid={`controladoria-${m.user_id}`}>
                <Switch checked={!!m.controladoria_indicada} onCheckedChange={(v) => indicarControladoria(m.user_id, v)} aria-label={`Controladoria para ${m.nome}`} />
                Vê a Controladoria
              </label>
            )}
            {admin && !m.eu && (confirmar === m.user_id ? (
              <span className="flex items-center gap-2">
                <Button size="sm" variant="outline" data-testid={`remover-confirmar-${m.user_id}`} onClick={() => remover(m.user_id)} className="text-red-700 border-red-200">Confirmar remoção</Button>
                <button type="button" onClick={() => setConfirmar(null)} className="text-xs text-stone-500 underline">cancelar</button>
              </span>
            ) : <Button size="sm" variant="outline" data-testid={`remover-${m.user_id}`} onClick={() => setConfirmar(m.user_id)} className="text-stone-600">Remover</Button>)}
            <div className="w-full text-xs text-stone-500" data-testid={`contagem-membro-${m.user_id}`}>{m.n_processos} {m.n_processos === 1 ? "processo visível" : "processos visíveis"} ao escritório · responsável por {m.n_responsavel ?? 0}</div>
          </div>
        ))}
      </section>

      <section aria-label="Identidade do escritório" className="mt-4 rounded-xl border border-stone-200 bg-white p-5" data-testid="escritorio-identidade">
        <h2 className="font-display text-base font-semibold text-stone-900 mb-3">Identidade nos avisos ao cliente</h2>
        <IdentidadeCard />
      </section>

      {(d.ex_membros || []).length > 0 && (
        <section aria-label="Quem saiu" className="mt-4 rounded-xl border border-dashed border-stone-300 bg-white/60 p-4" data-testid="escritorio-ex-membros">
          <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1">Saíram do escritório</div>
          <p className="text-[11px] text-stone-500 mb-2">O que estava na conta deles continua visível ao escritório. Passe os processos para quem vai cuidar deles.</p>
          <ul className="divide-y divide-stone-100">
            {d.ex_membros.map((x) => (
              <li key={x.user_id} data-testid={`ex-membro-${x.user_id}`} className="py-2 text-sm text-stone-700">{x.nome} <span className="text-stone-500">· {x.n_processos} {x.n_processos === 1 ? "processo visível" : "processos visíveis"} ao escritório · responsável por {x.n_responsavel}</span></li>
            ))}
          </ul>
        </section>
      )}

      <OabsEscritorio />

      {admin && (
        <section aria-label="Transferir processos" className="mt-6 rounded-xl border border-stone-200 bg-white p-5" data-testid="escritorio-transferir">
          <h2 className="font-display text-lg font-semibold text-stone-900 flex items-center gap-2 mb-1"><ArrowRightLeft className="w-4 h-4" /> Transferir processos</h2>
          <p className="text-xs text-stone-500 mb-3">Passa todos os processos de que uma pessoa é responsável para outra (por exemplo, quando alguém sai). Nada é apagado nem movido de conta.</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-stone-500">De
              <select data-testid="transferir-de" value={de} onChange={(e) => { setDe(e.target.value); setConfirmaTransf(false); }} className="mt-1 block h-10 rounded-md border border-stone-300 bg-white px-2 text-sm text-stone-900">
                <option value="">Escolha…</option>
                {[...d.membros, ...(d.ex_membros || [])].map((m) => <option key={m.user_id} value={m.user_id}>{m.nome} ({m.n_responsavel})</option>)}
              </select>
            </label>
            <label className="text-xs text-stone-500">Para
              <select data-testid="transferir-para" value={para} onChange={(e) => { setPara(e.target.value); setConfirmaTransf(false); }} className="mt-1 block h-10 rounded-md border border-stone-300 bg-white px-2 text-sm text-stone-900">
                <option value="">Escolha…</option>
                {d.membros.filter((m) => m.papel !== "apoio" && m.user_id !== de).map((m) => <option key={m.user_id} value={m.user_id}>{m.nome}</option>)}
              </select>
            </label>
            {!confirmaTransf
              ? <Button type="button" variant="outline" disabled={!de || !para || de === para} data-testid="transferir-continuar" onClick={() => setConfirmaTransf(true)}>Transferir…</Button>
              : <span className="flex items-center gap-2">
                <Button type="button" disabled={busy} data-testid="transferir-confirmar" onClick={transferir} className="bg-stone-900 text-white hover:bg-stone-800">Confirmar transferência</Button>
                <button type="button" onClick={() => setConfirmaTransf(false)} className="text-xs text-stone-500 underline">cancelar</button>
              </span>}
          </div>
        </section>
      )}

      {admin && (
        <section aria-label="Convidar pessoa" className="mt-6 rounded-xl border border-stone-200 bg-white p-5" data-testid="escritorio-convidar">
          <h2 className="font-display text-lg font-semibold text-stone-900 flex items-center gap-2 mb-3"><UserPlus className="w-4 h-4" /> Convidar pessoa</h2>
          <form onSubmit={convidar} className="flex flex-wrap items-end gap-3">
            <label className="flex-1 min-w-[220px] text-xs text-stone-500">E-mail
              <Input data-testid="convite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@escritorio.com.br" className="mt-1" />
            </label>
            <label className="text-xs text-stone-500">Papel
              <select data-testid="convite-papel" value={papel} onChange={(e) => setPapel(e.target.value)} className="mt-1 block h-10 rounded-md border border-stone-300 bg-white px-2 text-sm text-stone-900">
                {d.papeis.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
              </select>
            </label>
            <Button type="submit" disabled={busy || !email.includes("@")} data-testid="convite-enviar" className="bg-stone-900 text-white hover:bg-stone-800 gap-1.5"><Mail className="w-4 h-4" /> Enviar convite</Button>
          </form>
          <p className="mt-2 text-[11px] text-stone-500">{PAPEL_DICA[papel]} O convite vale 7 dias e só funciona com o e-mail informado.</p>
          {ultimoLink && (
            <div className="mt-3 rounded-lg bg-stone-50 border border-stone-200 p-3 text-xs" data-testid="convite-link-box">
              <div className="text-stone-600 mb-1">{ultimoLink.enviado ? `Enviado para ${ultimoLink.email}. ` : `O e-mail para ${ultimoLink.email} não saiu. `}Se preferir, mande o link por outro canal:</div>
              <div className="flex items-center gap-2"><code className="flex-1 truncate text-stone-800">{ultimoLink.link}</code>
                <Button size="sm" variant="outline" data-testid="convite-copiar" onClick={() => copiar(ultimoLink.link)} className="gap-1"><Copy className="w-3.5 h-3.5" /> Copiar</Button></div>
            </div>
          )}
          {d.convites.length > 0 && (
            <div className="mt-4" data-testid="convites-pendentes">
              <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1">Convites pendentes</div>
              <ul className="divide-y divide-stone-100">
                {d.convites.map((c) => (
                  <li key={c.invite_id} className="py-2 flex items-center gap-3 text-sm">
                    <span className="flex-1 min-w-0 truncate">{c.email} <span className="text-stone-500">· {rotulo(c.papel)}</span></span>
                    <button type="button" data-testid={`cancelar-${c.invite_id}`} onClick={() => cancelar(c.invite_id)} aria-label={`Cancelar convite de ${c.email}`} className="text-stone-400 hover:text-stone-700"><X className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <div className="mt-6 flex items-center gap-3 text-xs text-stone-500">
        <Building2 className="w-4 h-4" /> Quem trabalha no escritório enxerga e edita os processos uns dos outros em Processos e Agenda (seletor "Escritório").
        <Button variant="outline" size="sm" data-testid="escritorio-sair" onClick={sair} className="ml-auto gap-1.5 text-stone-600"><LogOut className="w-3.5 h-3.5" /> Sair do escritório</Button>
      </div>
    </div>
  );
}
